import React, { useEffect, useState, useMemo } from 'react';
import { getBookingReports, getBookingReportFilters } from '../../services/bookingService';
import { safeFormatDate } from '../../common/dateUtils';
import { 
  calculateBookingItemTotal, 
  calculateBookingTotal,
  calculateDiscountGiven,
  calculatePendingAmount
} from '../../utils/bookingCalculations';
import Footer from '../../components/Footer';

interface ReportFilters {
  clientId?: number;
  year?: number;
  month?: number;
  serviceId?: number;
  outsourcePersonId?: number;
  status?: string;
}

interface FilterOptions {
  clients: Array<{ id: number; fullName: string }>;
  services: Array<{ id: number; serviceName: string }>;
  outsourcePersons: Array<{ id: number; name: string }>;
  years: number[];
  statuses: string[];
}

export default function BookingReports() {
  const [bookings, setBookings] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>({
    totalBookings: 0,
    totalAmount: 0,
    totalDiscount: 0,
    totalBookingAmount: 0,
    totalPaidAmount: 0,
    totalPendingAmount: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    clients: [],
    services: [],
    outsourcePersons: [],
    years: [],
    statuses: [],
  });
  
  const [filters, setFilters] = useState<ReportFilters>({});
  const [expandedBookings, setExpandedBookings] = useState<Set<number>>(new Set());

  // Load filter options on mount
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const options = await getBookingReportFilters();
        setFilterOptions(options);
      } catch (error) {
        console.error('Failed to load filter options:', error);
      }
    };
    loadFilterOptions();
  }, []);

  // Load booking reports whenever filters change
  useEffect(() => {
    const loadReports = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getBookingReports(filters);
        
        // Check if data is directly an array (backend might return array instead of {bookings: [], summary: {}})
        if (Array.isArray(data)) {
          // Process bookings to calculate missing fields
          const processedBookings = data.map(booking => {
            // Calculate total amount from booking items using utility function
            const calculatedAmount = booking.amount || calculateBookingTotal(booking.bookingItems || []);
            
            // Get bookingAmount from backend
            const bookingAmt = booking.bookingAmount || 0;
            
            // Calculate discount using utility function
            const discount = booking.discountGiven !== null && booking.discountGiven !== undefined
              ? booking.discountGiven 
              : calculateDiscountGiven(calculatedAmount, bookingAmt);
            
            // Get paidAmount from backend or default to 0
            const paid = booking.paidAmount || 0;
            
            // Calculate pending amount using utility function
            const pending = calculatePendingAmount(bookingAmt, paid);
            
            return {
              ...booking,
              amount: calculatedAmount,
              discountGiven: discount,
              bookingAmount: bookingAmt,
              paidAmount: paid,
              pendingAmount: pending
            };
          });
          
          setBookings(processedBookings);
          
          // Calculate summary from processed bookings
          const calculatedSummary = {
            totalBookings: processedBookings.length,
            totalAmount: processedBookings.reduce((sum, b) => sum + (b.amount || 0), 0),
            totalDiscount: processedBookings.reduce((sum, b) => sum + (b.discountGiven || 0), 0),
            totalBookingAmount: processedBookings.reduce((sum, b) => sum + (b.bookingAmount || 0), 0),
            totalPaidAmount: processedBookings.reduce((sum, b) => sum + (b.paidAmount || 0), 0),
            totalPendingAmount: processedBookings.reduce((sum, b) => sum + (b.pendingAmount || 0), 0),
          };
          setSummary(calculatedSummary);
        } else {
          // Expected structure: {bookings: [], summary: {}}
          const bookingsData = data.bookings || [];
          const summaryData = data.summary || {
            totalBookings: 0,
            totalAmount: 0,
            totalDiscount: 0,
            totalBookingAmount: 0,
            totalPaidAmount: 0,
            totalPendingAmount: 0,
          };
          
          // Process bookings to calculate missing fields
          const processedBookings = bookingsData.map((booking: any) => {
            // Calculate total amount from booking items using utility function
            const calculatedAmount = booking.amount || calculateBookingTotal(booking.bookingItems || []);
            
            // Get bookingAmount from backend
            const bookingAmt = booking.bookingAmount || 0;
            
            // Calculate discount using utility function
            const discount = booking.discountGiven !== null && booking.discountGiven !== undefined
              ? booking.discountGiven 
              : calculateDiscountGiven(calculatedAmount, bookingAmt);
            
            // Get paidAmount from backend or default to 0
            const paid = booking.paidAmount || 0;
            
            // Calculate pending amount using utility function
            const pending = calculatePendingAmount(bookingAmt, paid);
            
            return {
              ...booking,
              amount: calculatedAmount,
              discountGiven: discount,
              bookingAmount: bookingAmt,
              paidAmount: paid,
              pendingAmount: pending
            };
          });
          
          setBookings(processedBookings);
          setSummary(summaryData);
        }
      } catch (error: any) {
        const errorMessage = error.response?.data?.message 
          || error.response?.data 
          || error.message 
          || 'Failed to load booking reports. Please try again.';
        setError(typeof errorMessage === 'string' ? errorMessage : 'Server error occurred');
        setBookings([]);
        setSummary({
          totalBookings: 0,
          totalAmount: 0,
          totalDiscount: 0,
          totalBookingAmount: 0,
          totalPaidAmount: 0,
          totalPendingAmount: 0,
        });
      } finally {
        setLoading(false);
      }
    };
    loadReports();
  }, [filters]);

  const handleFilterChange = (key: keyof ReportFilters, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value === '' || value === 'all' ? undefined : value,
    }));
  };

  const clearFilters = () => {
    setFilters({});
  };

  const toggleBookingExpansion = (bookingId: number) => {
    setExpandedBookings((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(bookingId)) {
        newSet.delete(bookingId);
      } else {
        newSet.add(bookingId);
      }
      return newSet;
    });
  };

  const hasActiveFilters = useMemo(() => {
    return Object.values(filters).some((v) => v !== undefined);
  }, [filters]);

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  return (
    <div className="p-6 pb-20 bg-background min-h-screen font-sans">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-semibold text-primary font-sans tracking-tight">
          Revenue & Booking Analytics
        </h1>
        <p className="text-secondary">
          Comprehensive view of your bookings, revenue, and pending payments
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <div className="bg-white shadow rounded p-4">
          <div className="text-secondary text-xs">Total Bookings</div>
          <div className="text-2xl font-semibold text-primary">{summary.totalBookings}</div>
        </div>
        <div className="bg-white shadow rounded p-4">
          <div className="text-secondary text-xs">Total Amount</div>
          <div className="text-xl font-semibold">₹ {summary.totalAmount?.toFixed(2)}</div>
        </div>
        <div className="bg-white shadow rounded p-4">
          <div className="text-secondary text-xs">Discount Given</div>
          <div className="text-xl font-semibold text-orange-600">₹ {summary.totalDiscount?.toFixed(2)}</div>
        </div>
        <div className="bg-white shadow rounded p-4">
          <div className="text-secondary text-xs">Booking Amount</div>
          <div className="text-xl font-semibold text-blue-600">₹ {summary.totalBookingAmount?.toFixed(2)}</div>
        </div>
        <div className="bg-white shadow rounded p-4">
          <div className="text-secondary text-xs">Paid Amount</div>
          <div className="text-xl font-semibold text-green-600">₹ {summary.totalPaidAmount?.toFixed(2)}</div>
        </div>
        <div className="bg-white shadow rounded p-4">
          <div className="text-secondary text-xs">Pending Amount</div>
          <div className="text-xl font-semibold text-red-600">₹ {summary.totalPendingAmount?.toFixed(2)}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded p-4 mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">Filters</h2>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="text-sm text-primary hover:underline"
            >
              Clear All Filters
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Client Filter */}
          <div>
            <label className="block text-sm text-secondary mb-1">Client</label>
            <select
              value={filters.clientId || ''}
              onChange={(e) => handleFilterChange('clientId', e.target.value ? Number(e.target.value) : '')}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            >
              <option value="">All Clients</option>
              {filterOptions.clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.fullName}
                </option>
              ))}
            </select>
          </div>

          {/* Year Filter */}
          <div>
            <label className="block text-sm text-secondary mb-1">Year</label>
            <select
              value={filters.year || ''}
              onChange={(e) => handleFilterChange('year', e.target.value ? Number(e.target.value) : '')}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            >
              <option value="">All Years</option>
              {filterOptions.years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          {/* Month Filter */}
          <div>
            <label className="block text-sm text-secondary mb-1">Month</label>
            <select
              value={filters.month || ''}
              onChange={(e) => handleFilterChange('month', e.target.value ? Number(e.target.value) : '')}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            >
              <option value="">All Months</option>
              {months.map((month) => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>
          </div>

          {/* Service Filter */}
          <div>
            <label className="block text-sm text-secondary mb-1">Service</label>
            <select
              value={filters.serviceId || ''}
              onChange={(e) => handleFilterChange('serviceId', e.target.value ? Number(e.target.value) : '')}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            >
              <option value="">All Services</option>
              {filterOptions.services.map((service) => (
                <option key={service.id} value={service.id}>
                  {service.serviceName}
                </option>
              ))}
            </select>
          </div>

          {/* Outsource Person Filter */}
          <div>
            <label className="block text-sm text-secondary mb-1">Outsource Person</label>
            <select
              value={filters.outsourcePersonId || ''}
              onChange={(e) => handleFilterChange('outsourcePersonId', e.target.value ? Number(e.target.value) : '')}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            >
              <option value="">All Outsource Persons</option>
              {filterOptions.outsourcePersons.map((person) => (
                <option key={person.id} value={person.id}>
                  {person.name}
                </option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm text-secondary mb-1">Status</label>
            <select
              value={filters.status || ''}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            >
              <option value="">All Statuses</option>
              {filterOptions.statuses.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Bookings Table */}
      <div className="bg-white shadow rounded p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">Booking Details</h2>
          {loading && <span className="text-sm text-secondary">Loading...</span>}
        </div>

        {error ? (
          <div className="text-center py-8">
            <div className="bg-red-50 border border-red-200 rounded p-4 mb-4">
              <p className="text-red-600 font-medium mb-2">⚠️ Error Loading Reports</p>
              <p className="text-red-500 text-sm mb-3">{error}</p>
              <p className="text-secondary text-xs">
                This is likely a backend issue. Please contact the development team.
              </p>
            </div>
            <button
              onClick={() => window.location.reload()}
              className="bg-primary text-white px-4 py-2 rounded hover:bg-primary/90"
            >
              Retry
            </button>
          </div>
        ) : bookings.length === 0 ? (
          <p className="text-secondary text-center py-8">
            {loading ? 'Loading reports...' : 'No bookings found matching the selected filters.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="text-left text-secondary text-sm bg-highlight">
                <tr>
                  <th className="py-2 px-3">Booking #</th>
                  <th className="py-2 px-3">Date</th>
                  <th className="py-2 px-3">Client</th>
                  <th className="py-2 px-3">Venue</th>
                  <th className="py-2 px-3">Status</th>
                  <th className="py-2 px-3 text-right">Amount</th>
                  <th className="py-2 px-3 text-right">Discount</th>
                  <th className="py-2 px-3 text-right">Booking Amt</th>
                  <th className="py-2 px-3 text-right">Paid</th>
                  <th className="py-2 px-3 text-right">Pending</th>
                  <th className="py-2 px-3 text-center">Details</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map((booking) => {
                  const isExpanded = expandedBookings.has(booking.id);
                  return (
                    <React.Fragment key={booking.id}>
                      <tr className="border-t hover:bg-highlight/40">
                        <td className="py-2 px-3 text-sm font-medium">{booking.bookingNumber}</td>
                        <td className="py-2 px-3 text-sm">{safeFormatDate(booking.eventDate)}</td>
                        <td className="py-2 px-3 text-sm">{booking.client?.fullName || '-'}</td>
                        <td className="py-2 px-3 text-sm">{booking.venue || '-'}</td>
                        <td className="py-2 px-3">
                          <span
                            className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                              booking.status === 'Booked'
                                ? 'bg-green-100 text-green-700'
                                : booking.status === 'Enquired'
                                ? 'bg-yellow-100 text-yellow-700'
                                : 'bg-red-100 text-red-700'
                            }`}
                          >
                            {booking.status}
                          </span>
                        </td>
                        <td className="py-2 px-3 text-sm text-right">₹ {booking.amount?.toFixed(2)}</td>
                        <td className="py-2 px-3 text-sm text-right text-orange-600">
                          ₹ {booking.discountGiven?.toFixed(2)}
                        </td>
                        <td className="py-2 px-3 text-sm text-right font-medium">
                          ₹ {booking.bookingAmount?.toFixed(2)}
                        </td>
                        <td className="py-2 px-3 text-sm text-right text-green-600">
                          ₹ {booking.paidAmount?.toFixed(2)}
                        </td>
                        <td className="py-2 px-3 text-sm text-right text-red-600 font-medium">
                          ₹ {booking.pendingAmount?.toFixed(2)}
                        </td>
                        <td className="py-2 px-3 text-center">
                          <button
                            onClick={() => toggleBookingExpansion(booking.id)}
                            className="text-primary hover:underline text-sm"
                          >
                            {isExpanded ? '▲ Hide' : '▼ Show'}
                          </button>
                        </td>
                      </tr>
                      {isExpanded && booking.bookingItems && booking.bookingItems.length > 0 && (
                        <tr className="bg-highlight/20">
                          <td colSpan={11} className="py-3 px-3">
                            <div className="pl-8">
                              <h4 className="text-sm font-semibold text-secondary mb-2">Service Breakdown:</h4>
                              <table className="min-w-full text-sm">
                                <thead className="text-left text-secondary text-xs">
                                  <tr>
                                    <th className="py-1">Date</th>
                                    <th className="py-1">Service</th>
                                    <th className="py-1">Count</th>
                                    <th className="py-1">Price Per Unit</th>
                                    <th className="py-1">Outsource Person</th>
                                    <th className="py-1 text-right">Amount</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {booking.bookingItems.map((item: any) => (
                                    <tr key={item.id} className="border-t border-gray-200">
                                      <td className="py-1">{safeFormatDate(item.bookingDate)}</td>
                                      <td className="py-1">{item.serviceName || '-'}</td>
                                      <td className="py-1">{item.serviceCount || 1}</td>
                                      <td className="py-1">₹ {(item.amount || 0).toFixed(2)}</td>
                                      <td className="py-1">{item.outsourcePerson?.name || 'Self'}</td>
                                      <td className="py-1 text-right">₹ {calculateBookingItemTotal(item.serviceCount || 1, item.amount || 0).toFixed(2)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
